/**
 * Renders pagination dots for the featured products carousel, where each dot
 * represents a "page" (the current group of simultaneously visible slides)
 * rather than a single slide.
 *
 * Root cause of the old inconsistent dot count (one dot per product on
 * first paint, correcting to the right page count only after the first
 * scroll/click): visible-slide count used to come from the slideshow
 * component's own `visibleSlides` list, which is populated asynchronously
 * by an IntersectionObserver (see assets/slideshow.js) and isn't reliably
 * ready yet when this component's first render runs - `visibleSlides`
 * would read as empty, falling back to 1 visible slide, so the page count
 * came out as "one page per product". The first `slideshow:select` event
 * (triggered by any scroll or click) ran after the observer had caught up,
 * which is why interacting with the slider appeared to "fix" it.
 *
 * Fix: compute visible-slide count directly from rendered DOM geometry
 * (scroller width / first card width) instead of the IntersectionObserver-
 * backed `visibleSlides`, and recompute it at every point layout could
 * have changed: first paint, DOMContentLoaded, window `load` (after images
 * finish loading and sizes are final), slider resize, and slide selection.
 */
class FeaturedProductsCarouselDots extends HTMLElement {
  /** @type {import('./slideshow.js').Slideshow | null} */
  #slideshow = null;

  #controller = new AbortController();

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  #currentPage = 0;

  connectedCallback() {
    const slideshow = this.closest('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    this.#slideshow = /** @type {any} */ (slideshow);

    const { signal } = this.#controller;
    slideshow.addEventListener('slideshow:select', this.#handleSelect, { signal });

    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(slideshow);

    const renderNow = () => this.#render();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', renderNow, { signal, once: true });
    }

    if (document.readyState === 'complete') {
      renderNow();
    } else {
      window.addEventListener('load', renderNow, { signal, once: true });
    }

    requestAnimationFrame(renderNow);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Visible-slide count measured from actual rendered dimensions (scroller
   * clientWidth / first card width) - see the class doc comment for why
   * this replaces `slideshow.visibleSlides`.
   */
  get #visibleCount() {
    const scroller = this.#slideshow?.refs?.scroller;
    const slides = this.#slideshow?.slides;
    if (!scroller || !slides?.length) return 1;

    const containerWidth = scroller.clientWidth;
    const cardWidth = slides[0].getBoundingClientRect().width;

    // Guards against a 0 clientWidth (not laid out yet, e.g. a parent with
    // display: none) and any resulting division producing 0/NaN/Infinity -
    // the ResizeObserver in connectedCallback re-renders once real
    // dimensions are available, so falling back to 1 here is temporary.
    if (!Number.isFinite(containerWidth) || !Number.isFinite(cardWidth) || containerWidth <= 0 || cardWidth <= 0) {
      return 1;
    }

    const count = Math.round(containerWidth / cardWidth);
    return Number.isFinite(count) && count > 0 ? count : 1;
  }

  get #totalSlides() {
    return this.#slideshow?.slides?.length || 0;
  }

  get #pageCount() {
    return Math.max(Math.ceil(this.#totalSlides / this.#visibleCount), 1);
  }

  /**
   * @param {Event} event
   */
  #handleSelect = (event) => {
    const index = /** @type {CustomEvent} */ (event).detail?.index ?? 0;

    this.#currentPage = Math.floor(index / this.#visibleCount);
    this.#render();
  };

  #render() {
    const pageCount = this.#pageCount;

    if (this.childElementCount !== pageCount) {
      this.replaceChildren(
        ...Array.from({ length: pageCount }, (_, page) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'featured-products-carousel__dot button button-unstyled';
          button.setAttribute('aria-label', `${page + 1}`);
          button.addEventListener('click', () => this.#slideshow?.select(page * this.#visibleCount));
          return button;
        })
      );
    }

    this.#currentPage = Math.min(this.#currentPage, pageCount - 1);

    Array.from(this.children).forEach((dot, page) => {
      dot.setAttribute('aria-selected', String(page === this.#currentPage));
    });
  }
}

if (!customElements.get('featured-products-carousel-dots')) {
  customElements.define('featured-products-carousel-dots', FeaturedProductsCarouselDots);
}
